import { motion } from "framer-motion";
import { Reveal, Kicker, EASE } from "./motion";
import CountUp from "./CountUp";

function InsightCard({ label, to, prefix = "", suffix = "", text, note, delay = 0 }) {
  return (
    <div className="relative bg-paper glass border border-verlo/10 rounded-xl p-7 md:p-8 shadow-soft">
      <p className="text-[11px] tracking-[0.2em] uppercase text-verlo/40 mb-5">{label}</p>
      {to !== undefined ? (
        <p className="font-display font-light text-4xl md:text-5xl text-brass leading-none">
          <CountUp to={to} prefix={prefix} suffix={suffix} />
        </p>
      ) : (
        <p className="font-display font-light text-3xl md:text-4xl text-verlo leading-none">
          {text}
        </p>
      )}
      <motion.span
        className="block mt-4 h-px bg-verlo/15"
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        viewport={{ once: true }}
        style={{ transformOrigin: "left" }}
        transition={{ duration: 0.8, ease: EASE, delay: delay + 0.3 }}
      />
      <p className="mt-4 text-sm md:text-base leading-relaxed text-verlo">{note}</p>
    </div>
  );
}

function Bar({ h, active }) {
  return (
    <div
      className={`w-full rounded-sm ${active ? "bg-brass" : "bg-verlo/15"}`}
      style={{ height: `${h}%` }}
    />
  );
}

function DashboardMock() {
  const bars = [42, 58, 35, 66, 74, 51, 88, 61, 47, 79, 55, 92];
  return (
    <div className="bg-paper border border-verlo/10 rounded-2xl shadow-soft-lg p-8 md:p-20 w-full">
      <div className="flex items-center justify-between mb-14">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase text-verlo/40">Executive Dashboard</p>
          <p className="font-display font-light text-3xl md:text-6xl text-verlo mt-3">
            Friday, dinner service
          </p>
        </div>
        <span className="text-xs text-brass border border-brass/40 rounded-full px-3 py-1">Live</span>
      </div>

      <div className="grid grid-cols-3 gap-6 md:gap-12 mb-16">
        {[
          { k: "Covers tonight", to: 214, dp: 0, pre: "" },
          { k: "Average spend", to: 47.2, dp: 2, pre: "£" },
          { k: "Labour vs sales", to: 26, dp: 0, sfx: "%" },
        ].map((s) => (
          <div key={s.k} className="border-t border-verlo/15 pt-4">
            <p className="text-[11px] tracking-wide uppercase text-verlo/40">{s.k}</p>
            <p className="font-display font-light text-3xl md:text-7xl text-verlo mt-3">
              <CountUp to={s.to} decimals={s.dp} prefix={s.pre || ""} suffix={s.sfx || ""} />
            </p>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-3 gap-12">
        <div className="md:col-span-2">
          <p className="text-[11px] tracking-wide uppercase text-verlo/40 mb-6">Sales by hour</p>
          <div className="flex items-end gap-2 md:gap-3 h-48 md:h-64">
            {bars.map((h, i) => (
              <Bar key={i} h={h} active={i === 6} />
            ))}
          </div>
        </div>
        <div>
          <p className="text-[11px] tracking-wide uppercase text-verlo/40 mb-6">Priority AI</p>
          <ul className="space-y-6 text-sm md:text-base">
            <li className="flex gap-3">
              <span className="text-brass">01</span>
              <span className="text-verlo/80">Table 12 waiting on desserts, nine minutes</span>
            </li>
            <li className="flex gap-3">
              <span className="text-brass">02</span>
              <span className="text-verlo/80">Sea bass low, six portions left</span>
            </li>
            <li className="flex gap-3">
              <span className="text-brass">03</span>
              <span className="text-verlo/80">Section three over on labour this hour</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default function Intelligence() {
  return (
    <section
      id="intelligence"
      data-testid="chapter-intelligence"
      className="py-28 md:py-44 border-t border-verlo/10 bg-sand"
    >
      <div className="px-6 md:px-12">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <div className="flex items-baseline gap-4 mb-8">
              <span className="font-display text-brass text-lg">III</span>
              <Kicker>Intelligence</Kicker>
            </div>
            <h2 className="font-display font-light text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[1.1] text-verlo max-w-3xl">
              Know where your profit went before breakfast.
            </h2>
          </Reveal>

          <div className="mt-16 md:mt-24 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Reveal>
              <InsightCard label="Revenue" to={14} prefix="+" suffix="%" note="Up on last Friday, driven by the bar." />
            </Reveal>
            <Reveal>
              <InsightCard label="Draft beer" text="Margin falling" note="Supplier prices rose this week. Worth a look." delay={0.1} />
            </Reveal>
            <Reveal>
              <InsightCard label="Brownies" to={22} prefix="+" suffix="%" note="Attachment rate up. The upsell is working." delay={0.2} />
            </Reveal>
            <Reveal>
              <InsightCard label="Labour" text="Over budget" note="Tuesday evenings are running hot." delay={0.3} />
            </Reveal>
          </div>

          <Reveal>
            <p className="mt-16 md:mt-20 text-xl md:text-2xl leading-loose text-verlo max-w-2xl">
              Not another wall of charts. The one thing worth your attention, while there is
              still a night left to change it.
            </p>
          </Reveal>
        </div>
      </div>

      {/* enormous product centrepiece */}
      <Reveal>
        <div className="mt-16 md:mt-24 px-4 md:px-8">
          <div className="mx-auto max-w-[1680px]">
            <DashboardMock />
          </div>
        </div>
      </Reveal>
    </section>
  );
}
